import React, {
  FC, useRef, ChangeEvent, RefObject, KeyboardEvent, useState, useEffect,
} from 'react';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import { resendTfaCode, verifyPhoneViaToken } from '../../../../structures/requests/user';

interface Props {
  setStep(step: number): void;
}

interface FormValues {
  token: string[];
}

const initialValues: FormValues = {
  token: [],
};

const onlyDigitsRegExp = /^[0-9\b]+$/;

const handleOTPInput = (handleChange: (handleChangeEvent: ChangeEvent<HTMLInputElement>) =>
  void, forwardRef: RefObject<HTMLInputElement> | null = null,
backwardRef: RefObject<HTMLInputElement> | null = null) => (
  event: ChangeEvent<HTMLInputElement>,
) => {
  if (event.target.value === '' || onlyDigitsRegExp.test(event.target.value)) {
    handleChange(event);
    if (forwardRef?.current && event.target.value.length === 1) {
      forwardRef.current.focus();
    }
    if (backwardRef?.current && event.target.value.length === 0) {
      backwardRef.current.focus();
    }
  }
};

const handleKeyDown = (
  index: number,
  formValues: FormValues,
  refs: RefObject<HTMLInputElement>[],
) => (e: KeyboardEvent<HTMLInputElement>) => {
  // If backspace is pressed and the current field is empty
  if ((e.key === 'Backspace' || e.key === 'Delete') && !formValues.token[index]) {
    e.preventDefault();
    // Focus on the previous input if it exists
    if (index > 0) {
      refs[index - 1].current?.focus();
    }
  } else if (e.key === 'ArrowRight' && index < refs.length - 1) {
  // If right arrow is pressed, move to next input
    e.preventDefault();
    refs[index + 1].current?.focus();
  } else if (e.key === 'ArrowLeft' && index > 0) {
  // If left arrow is pressed, move to previous input
    e.preventDefault();
    refs[index - 1].current?.focus();
  }
};

const RETRY_TIMEOUT = 60 * 1000;
const TFASecondStep: FC<Props> = ({ setStep }) => {
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [canRetry, setCanRetry] = useState(false);
  const [retryTimeout, setRetryTimeout] = useState<NodeJS.Timeout | undefined>();
  const [countdown, setCountdown] = useState(RETRY_TIMEOUT / 1000);

  const clearRetryTimeout = () => {
    if (retryTimeout) {
      clearTimeout(retryTimeout);
    }
  };

  const handleSetRetryTimeout = () => {
    setCanRetry(false);
    setCountdown(RETRY_TIMEOUT / 1000);
    setRetryTimeout(
      setTimeout(() => {
        setCanRetry(true);
      }, RETRY_TIMEOUT),
    );
  };

  const handleReSendCode = async () => {
    setApiErrorMessage(null);
    await resendTfaCode('sms').then(() => {
      handleSetRetryTimeout();
    }).catch(
      (e) => {
        setApiErrorMessage('Failed to resend verification code. Please try again.');
      },
    );
  };

  useEffect(() => {
    handleSetRetryTimeout();
    const interval = setInterval(() => {
      setCountdown((prevCountdown) => (prevCountdown > 0 ? prevCountdown - 1 : 0));
    }, 1000);
    return () => {
      clearRetryTimeout();
      clearInterval(interval);
    };
  }, []);

  const {
    handleSubmit, values: formValues, handleBlur, handleChange,
  } = useFormik<FormValues>({
    initialValues,
    onSubmit: (values) => {
      const token = values.token.join('');
      setIsLoading(true);

      verifyPhoneViaToken(token)
        .then((e) => {
          if (e) {
            clearRetryTimeout();
            setStep(3);
          }
        })
        .catch((e) => {
          if (e) {
            setApiErrorMessage('This verification code was not valid. Please try again.');
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  const firstFieldRef = useRef<HTMLInputElement>(null);
  const secondFieldRef = useRef<HTMLInputElement>(null);
  const thirdFieldRef = useRef<HTMLInputElement>(null);
  const fourthFieldRef = useRef<HTMLInputElement>(null);
  const fifthFieldRef = useRef<HTMLInputElement>(null);

  const refs = [firstFieldRef, secondFieldRef, thirdFieldRef, fourthFieldRef, fifthFieldRef];

  return (
    <form className="modal-container" onSubmit={handleSubmit}>
      <h2 className="sub-title">
        Enter verification code
      </h2>
      <p className="description-paragraph">
        Authorize with 5-digit code sent
        to your phone.
      </p>
      <div className="otp-input-container">
        <Form.Control
          ref={firstFieldRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          onKeyDown={handleKeyDown(0, formValues, refs)}
          name="token[0]"
          value={formValues.token[0] || ''}
          onChange={handleOTPInput(handleChange, secondFieldRef)}
          onBlur={handleBlur}
          className="otp-input"
        />
        <Form.Control
          ref={secondFieldRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          onKeyDown={handleKeyDown(1, formValues, refs)}
          name="token[1]"
          value={formValues.token[1] || ''}
          onChange={handleOTPInput(handleChange, thirdFieldRef, firstFieldRef)}
          onBlur={handleBlur}
          className="otp-input"
        />
        <Form.Control
          ref={thirdFieldRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          onKeyDown={handleKeyDown(2, formValues, refs)}
          name="token[2]"
          value={formValues.token[2] || ''}
          onChange={handleOTPInput(handleChange, fourthFieldRef, secondFieldRef)}
          onBlur={handleBlur}
          className="otp-input"
        />
        <Form.Control
          ref={fourthFieldRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          onKeyDown={handleKeyDown(3, formValues, refs)}
          name="token[3]"
          value={formValues.token[3] || ''}
          onChange={handleOTPInput(handleChange, fifthFieldRef, thirdFieldRef)}
          onBlur={handleBlur}
          className="otp-input"
        />
        <Form.Control
          ref={fifthFieldRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          onKeyDown={handleKeyDown(4, formValues, refs)}
          name="token[4]"
          value={formValues.token[4] || ''}
          onChange={handleOTPInput(handleChange, null, fourthFieldRef)}
          onBlur={handleBlur}
          className="otp-input"
        />
      </div>
      {
        apiErrorMessage && (
          <p className="text-danger mt-2 text-center">
            {apiErrorMessage}
          </p>
        )
      }
      <Button
        type="submit"
        variant="primary"
        className="button-base"
        disabled={isLoading || formValues.token.filter(Boolean).length < 5}
      >
        Complete authentication
      </Button>
      <Button
        type="button"
        variant="outline-primary"
        disabled={!canRetry}
        onClick={handleReSendCode}
        className="mt-2 button-base"
      >
        {canRetry ? 'Re-send the code' : `Re-send the code (${countdown})`}
      </Button>
    </form>
  );
};

export default TFASecondStep;
